import React, { useState, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';

interface AutocompleteTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  loading?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const AutocompleteTextField: React.FC<AutocompleteTextFieldProps> = ({
  label,
  value,
  onChange,
  options,
  loading = false,
  required = false,
  placeholder = '',
  disabled = false,
  error = false,
  helperText = ''
}) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
    // Update parent component immediately when typing
    onChange(newInputValue);
  };

  const handleChange = (event: any, newValue: string | null) => {
    const selectedValue = newValue || '';
    onChange(selectedValue);
    setInputValue(selectedValue);
  };

  const handleBlur = () => {
    // Ensure the current input value is saved when user leaves the field
    onChange(inputValue);
  };

  // Filter options based on input value (case insensitive)
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <Autocomplete
      freeSolo
      options={filteredOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      loading={loading}
      disabled={disabled}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Box>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Box>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
              '& fieldset': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              '&.Mui-focused': {
                color: 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              color: 'text.primary',
            },
            '& .MuiFormHelperText-root': {
              color: error ? 'error.main' : 'text.secondary',
            }
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {option}
          </Typography>
        </Box>
      )}
      ListboxProps={{
        style: {
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '8px',
          maxHeight: '200px',
        }
      }}
      PaperComponent={({ children, ...other }) => (
        <Box
          {...other}
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            '& .MuiAutocomplete-option': {
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-focused': {
                backgroundColor: 'action.selected',
              },
            }
          }}
        >
          {children}
        </Box>
      )}
      noOptionsText={
        <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
          {inputValue ? `Tidak ada saran untuk "${inputValue}" - Anda tetap bisa mengetik nama pemain baru` : 'Tidak ada nama pemain tersedia'}
        </Typography>
      }
      loadingText={
        <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
          Memuat daftar nama pemain...
        </Typography>
      }
    />
  );
};

export default AutocompleteTextField;
